import { useEffect, useRef } from 'react';
import { MILESTONE_ICON_UNICODE, MILESTONE_ICON } from '../constants/types';
import { useTranslation } from '../i18n';
import './DayContextMenu.scss';

/**
 * Context menu that appears on right-click or single-click of a day cell.
 * Provides options to add an event or milestone on the selected date.
 * 
 * @param {Object} props
 * @param {Date} props.date - The date that was clicked
 * @param {Object} props.position - Screen position for menu
 * @param {number} props.position.x - X coordinate (pixels)
 * @param {number} props.position.y - Y coordinate (pixels)
 * @param {Function} props.onAddEvent - Handler for "Add Event" option
 * @param {Function} props.onAddMilestone - Handler for "Add Milestone" option
 * @param {Function} props.onClose - Handler for closing the menu
 */
function DayContextMenu({ date, position, onAddEvent, onAddMilestone, onClose }) {
  const { t, formatDate } = useTranslation();
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <div 
      className="day-context-menu" 
      ref={menuRef}
      style={{ 
        top: `${position.y}px`, 
        left: `${position.x}px` 
      }}
    >
      <div className="day-context-menu__header">
        {date ? formatDate(date) : ''}
      </div>
      <div className="day-context-menu__items">
        <button 
          className="day-context-menu__item"
          onClick={() => {
            onAddEvent();
            onClose();
          }}
        >
          <span className="day-context-menu__icon">📅</span>
          {t('contextMenu.addEvent')}
        </button>
        <button 
          className="day-context-menu__item"
          onClick={() => {
            onAddMilestone();
            onClose();
          }}
        >
          <span className="day-context-menu__icon">{MILESTONE_ICON_UNICODE[MILESTONE_ICON.FLAG]}</span>
          {t('contextMenu.addMilestone')}
        </button>
      </div>
    </div>
  );
}

export default DayContextMenu;
